import { Capacitor } from '@capacitor/core'
import {
  CustomProvider,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check'
import { firebaseApp } from './firebase'

declare const __FIREBASE_APPCHECK_DEBUG_TOKEN__: string

type DebugTokenExchangeResponse = {
  token: string
  ttl: string
}

let initialization: Promise<void> | null = null
let purchasesInitialization: Promise<void> | null = null

export function initializeNativePurchases(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve()
  if (purchasesInitialization) return purchasesInitialization
  purchasesInitialization = (async () => {
    const apiKey = Capacitor.getPlatform() === 'ios'
      ? import.meta.env.VITE_REVENUECAT_IOS_PUBLIC_KEY
      : import.meta.env.VITE_REVENUECAT_ANDROID_PUBLIC_KEY
    if (!apiKey) throw new Error('Store membership configuration is missing.')
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    await Purchases.configure({ apiKey })
  })().catch((error) => {
    purchasesInitialization = null
    throw error
  })
  return purchasesInitialization
}

async function exchangeLocalDebugToken(debugToken: string) {
  const { apiKey, appId, projectId } = firebaseApp.options
  if (!apiKey || !appId || !projectId) {
    throw new Error('Firebase App Check yerel geliştirme yapılandırması eksik.')
  }

  const endpoint = new URL(
    `https://firebaseappcheck.googleapis.com/v1/projects/${encodeURIComponent(projectId)}/apps/${encodeURIComponent(appId)}:exchangeDebugToken`,
  )
  endpoint.searchParams.set('key', apiKey)

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ debug_token: debugToken }),
  })
  if (!response.ok) {
    throw new Error(`Firebase App Check debug token değişimi başarısız: ${response.status}`)
  }

  const result = await response.json() as DebugTokenExchangeResponse
  const ttlSeconds = Number.parseFloat(result.ttl)
  return {
    token: result.token,
    expireTimeMillis: Date.now() + (Number.isFinite(ttlSeconds) ? ttlSeconds : 3_600) * 1_000,
  }
}

export function initializePlatformServices(): Promise<void> {
  if (initialization) return initialization
  initialization = configurePlatformServices().catch((error) => {
    initialization = null
    throw error
  })
  return initialization
}

async function configurePlatformServices() {
  const siteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY

  if (Capacitor.isNativePlatform()) {
    const [{ FirebaseAnalytics }, { FirebaseAppCheck }] = await Promise.all([
      import('@capacitor-firebase/analytics'),
      import('@capacitor-firebase/app-check'),
    ])
    if (Capacitor.getPlatform() === 'ios') {
      // App Check's provider must be installed before FirebaseApp.configure()
      // on iOS. AppDelegate owns that setup; only toggle refresh here.
      await FirebaseAppCheck.setTokenAutoRefreshEnabled({ enabled: true })
    } else {
      await FirebaseAppCheck.initialize({
        isTokenAutoRefreshEnabled: true,
      })
    }
    initializeAppCheck(firebaseApp, {
      provider: new CustomProvider({
        getToken: async () => {
          console.log('[VOXNELA_APP_CHECK] Web SDK requested native token')
          try {
            const result = await FirebaseAppCheck.getToken({ forceRefresh: false })
            console.log('[VOXNELA_APP_CHECK] Native token delivered to Web SDK')
            return {
              token: result.token,
              expireTimeMillis:
                result.expireTimeMillis ?? Date.now() + 50 * 60 * 1000,
            }
          } catch (error) {
            console.error('[VOXNELA_APP_CHECK] Native token request failed', error)
            throw error
          }
        },
      }),
      isTokenAutoRefreshEnabled: true,
    })

    void FirebaseAnalytics.setEnabled({ enabled: true }).catch((error) => {
      console.warn('[VOXNELA_ANALYTICS]', error)
    })
    return
  }

  const isLocalDevelopment =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)
  const localDebugToken = isLocalDevelopment
    ? __FIREBASE_APPCHECK_DEBUG_TOKEN__
    : ''

  const webProvider = localDebugToken
    ? new CustomProvider({
        getToken: () => exchangeLocalDebugToken(localDebugToken),
      })
    : siteKey
      ? new ReCaptchaEnterpriseProvider(siteKey)
      : null

  if (webProvider) {
    initializeAppCheck(firebaseApp, {
      provider: webProvider,
      isTokenAutoRefreshEnabled: true,
    })
  }
}
